import { useState, useMemo, useEffect } from 'react';
import Papa from 'papaparse';
import { Download } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Textarea } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { saveAs } from 'file-saver';
import { useApp } from '@/store/app';
import { readFileAsText } from '@/lib/utils';
import { toast } from 'sonner';

const SAMPLE = `region,product,quarter,units,revenue
North,Widget A,Q1,120,180000
North,Widget B,Q1,80,160000
South,Widget A,Q1,90,135000
South,Widget B,Q1,110,220000
North,Widget A,Q2,150,225000
North,Widget B,Q2,100,200000
South,Widget A,Q2,130,195000
South,Widget B,Q2,140,280000
East,Widget A,Q1,60,90000
East,Widget B,Q1,70,140000
East,Widget A,Q2,85,127500
East,Widget B,Q2,95,190000`;

type Aggregator = 'sum' | 'avg' | 'count' | 'min' | 'max';

function aggregate(values: number[], op: Aggregator): number {
  if (op === 'count') return values.length;
  if (!values.length) return 0;
  const nums = values.filter((v) => !Number.isNaN(v));
  if (op === 'sum') return nums.reduce((a, b) => a + b, 0);
  if (op === 'avg') return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
  if (op === 'min') return Math.min(...nums);
  if (op === 'max') return Math.max(...nums);
  return 0;
}

export function CsvPivot() {
  const { pendingFile, setPendingFile } = useApp();
  const [raw, setRaw] = useState(SAMPLE);
  const [rowKey, setRowKey] = useState('region');
  const [colKey, setColKey] = useState('quarter');
  const [valueKey, setValueKey] = useState('revenue');
  const [op, setOp] = useState<Aggregator>('sum');

  useEffect(() => {
    if (pendingFile && /\.csv$/i.test(pendingFile.name)) {
      readFileAsText(pendingFile).then(setRaw);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parsed = useMemo(() => {
    if (!raw.trim()) return { headers: [] as string[], rows: [] as Record<string, string>[] };
    const r = Papa.parse<Record<string, string>>(raw, { header: true, skipEmptyLines: true });
    return { headers: (r.meta.fields ?? []) as string[], rows: r.data };
  }, [raw]);

  // Default field selections when headers change
  useEffect(() => {
    if (parsed.headers.length === 0) return;
    if (!parsed.headers.includes(rowKey)) setRowKey(parsed.headers[0]);
    if (!parsed.headers.includes(colKey)) setColKey(parsed.headers[1] ?? parsed.headers[0]);
    if (!parsed.headers.includes(valueKey)) setValueKey(parsed.headers[parsed.headers.length - 1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed.headers]);

  const pivot = useMemo(() => {
    if (!parsed.rows.length || !rowKey || !colKey || !valueKey)
      return { rowVals: [] as string[], colVals: [] as string[], data: {} as Record<string, Record<string, number>>, totals: { row: {} as Record<string, number>, col: {} as Record<string, number>, grand: 0 } };
    const data: Record<string, Record<string, number[]>> = {};
    const rowVals = new Set<string>();
    const colVals = new Set<string>();
    for (const r of parsed.rows) {
      const rk = String(r[rowKey] ?? '');
      const ck = String(r[colKey] ?? '');
      rowVals.add(rk);
      colVals.add(ck);
      const v = parseFloat(r[valueKey] ?? '0');
      (data[rk] ??= {})[ck] ??= [];
      data[rk][ck].push(Number.isFinite(v) ? v : 0);
    }
    const out: Record<string, Record<string, number>> = {};
    const rowTotals: Record<string, number> = {};
    const colTotals: Record<string, number> = {};
    let grand = 0;
    [...rowVals].forEach((rk) => {
      out[rk] = {};
      [...colVals].forEach((ck) => {
        const arr = data[rk]?.[ck] ?? [];
        const v = aggregate(arr, op);
        out[rk][ck] = v;
        rowTotals[rk] = (rowTotals[rk] ?? 0) + v;
        colTotals[ck] = (colTotals[ck] ?? 0) + v;
        grand += v;
      });
    });
    return {
      rowVals: [...rowVals].sort(),
      colVals: [...colVals].sort(),
      data: out,
      totals: { row: rowTotals, col: colTotals, grand },
    };
  }, [parsed, rowKey, colKey, valueKey, op]);

  function exportCsv() {
    const lines: string[] = [];
    lines.push([`${rowKey} \\ ${colKey}`, ...pivot.colVals, 'Total'].join(','));
    pivot.rowVals.forEach((rk) => {
      const cells = pivot.colVals.map((ck) => pivot.data[rk]?.[ck] ?? 0);
      lines.push([rk, ...cells, pivot.totals.row[rk]].map(String).join(','));
    });
    lines.push(['Total', ...pivot.colVals.map((ck) => pivot.totals.col[ck]), pivot.totals.grand].map(String).join(','));
    saveAs(new Blob([lines.join('\n')], { type: 'text/csv' }), 'pivot.csv');
    toast.success('Saved');
  }

  const max = Math.max(1, ...pivot.rowVals.flatMap((rk) => pivot.colVals.map((ck) => pivot.data[rk]?.[ck] ?? 0)));

  return (
    <div>
      <ToolHeader
        toolId="csv-pivot"
        title="CSV pivot table"
        description="Cross-tab any CSV — pick rows, columns, value, and aggregation. Heat-mapped for quick scanning."
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 space-y-3">
          <Card className="space-y-3">
            <Field label="Rows">
              <Select
                value={rowKey}
                onChange={(e) => setRowKey(e.target.value)}
                options={parsed.headers.map((h) => ({ value: h, label: h }))}
              />
            </Field>
            <Field label="Columns">
              <Select
                value={colKey}
                onChange={(e) => setColKey(e.target.value)}
                options={parsed.headers.map((h) => ({ value: h, label: h }))}
              />
            </Field>
            <Field label="Value">
              <Select
                value={valueKey}
                onChange={(e) => setValueKey(e.target.value)}
                options={parsed.headers.map((h) => ({ value: h, label: h }))}
              />
            </Field>
            <Field label="Aggregation">
              <Select
                value={op}
                onChange={(e) => setOp(e.target.value as Aggregator)}
                options={[
                  { value: 'sum', label: 'Sum' },
                  { value: 'avg', label: 'Average' },
                  { value: 'count', label: 'Count' },
                  { value: 'min', label: 'Min' },
                  { value: 'max', label: 'Max' },
                ]}
              />
            </Field>
            <Button variant="primary" className="w-full" onClick={exportCsv} disabled={!pivot.rowVals.length}>
              <Download size={11} />
              Export pivot
            </Button>
          </Card>
          <Card className="!p-0">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              Source CSV
            </div>
            <Textarea
              mono
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              className="!border-0 !rounded-t-none min-h-[180px] !bg-transparent focus-visible:!ring-0 text-[12px]"
            />
          </Card>
        </div>

        <Card className="lg:col-span-3 !p-0 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <span className="text-xs font-medium text-fg-muted">
              {op.toUpperCase()} of {valueKey}
            </span>
            <span className="text-2xs text-fg-subtle font-mono">
              {pivot.rowVals.length} × {pivot.colVals.length}
            </span>
          </div>
          <div className="overflow-auto max-h-[560px]">
            <table className="w-full text-sm">
              <thead className="bg-bg-inset sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-mono font-semibold text-fg-muted border-b border-border whitespace-nowrap">
                    {rowKey} \ {colKey}
                  </th>
                  {pivot.colVals.map((c) => (
                    <th
                      key={c}
                      className="text-right px-3 py-2 text-xs font-mono font-semibold text-fg-muted border-b border-border whitespace-nowrap"
                    >
                      {c}
                    </th>
                  ))}
                  <th className="text-right px-3 py-2 text-xs font-mono font-semibold border-b border-border whitespace-nowrap bg-bg-inset">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {pivot.rowVals.map((rk) => (
                  <tr key={rk} className="hover:bg-surface-hover">
                    <td className="px-3 py-2 border-b border-border font-medium whitespace-nowrap">{rk}</td>
                    {pivot.colVals.map((ck) => {
                      const v = pivot.data[rk]?.[ck] ?? 0;
                      const ratio = v / max;
                      return (
                        <td
                          key={ck}
                          className="px-3 py-2 border-b border-border text-right font-mono tabular-nums"
                          style={{
                            background: `hsl(var(--accent) / ${ratio * 0.18})`,
                          }}
                        >
                          {v.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 border-b border-border text-right font-mono tabular-nums font-semibold bg-bg-inset">
                      {pivot.totals.row[rk]?.toLocaleString(undefined, { maximumFractionDigits: 2 }) ?? '—'}
                    </td>
                  </tr>
                ))}
                <tr className="bg-bg-inset">
                  <td className="px-3 py-2 border-b border-border font-semibold">Total</td>
                  {pivot.colVals.map((ck) => (
                    <td
                      key={ck}
                      className="px-3 py-2 border-b border-border text-right font-mono tabular-nums font-semibold"
                    >
                      {pivot.totals.col[ck]?.toLocaleString(undefined, { maximumFractionDigits: 2 }) ?? '—'}
                    </td>
                  ))}
                  <td className="px-3 py-2 border-b border-border text-right font-mono tabular-nums font-semibold text-accent">
                    {pivot.totals.grand.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default CsvPivot;
